import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Palette,
  Film,
  Layers,
  Play,
  Zap,
  ChevronDown,
  Image as ImageIcon,
  MousePointer,
  MonitorPlay,
  Briefcase,
  Star,
  User,
} from "lucide-react";
import {
  FaFileImage,
  // FaPaintBrush,
  FaMagic,
  FaCamera,
  FaLayerGroup,
  FaFont,
  FaPhotoVideo,
  FaPalette,
  FaBriefcase,
  FaFilm,
  FaLaptopCode,
  FaCameraRetro,
  FaBullhorn,
  // FaLayerGroup,
  // FaMagic,
  // FaFont,
  FaPaintBrush,
  // FaCamera,
  FaPenNib,
  FaVideo,
  FaCube,
  FaRunning,
  // FaFileImage,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import AdobePic from "./Img/adobe3.png";

const IMAGES = {
  hero: "/assets/adobe-shares-slump-7-as-investors-skeptical-of-quicker-ai-adoption-returns.webp",
  design: "/assets/adobe-programs-1000x450.jpg",
  animation: "/assets/360_F_568451180_PJ3moxPIsp4QwvQTpzlsuZWWcI6ZHnRD.jpg",
};

const IMAGES1 = {
  hero: "/assets/Thumb-Photoshop-2.jpg",
  workflow: "/assets/adobe-photoshop-photo-editing.jpg",
};

const workflow = [
  "Import and organize raw images.",
  "Retouch photos with healing tools.",
  "Apply filters, effects, and layer adjustments.",
  "Add creative text and vector elements.",
  "Export for print, digital, and web formats.",
];

export default function Adobep() {
  useEffect(() => {
    AOS.init({ duration: 800, once: true, offset: 80 });
    window.scrollTo(0, 0);
  }, []);

  const topics = [
    {
      icon: <FaPaintBrush />,
      title: "Basic Tools & Interface",
      description:
        "Get familiar with Photoshop's workspace and essential tools.",
    },
    {
      icon: <FaCamera />,
      title: "Image Editing",
      description:
        "Retouch, crop, and enhance photos with professional techniques.",
    },
    {
      icon: <FaLayerGroup />,
      title: "Layers & Masks",
      description: "Master non-destructive editing using layers and masking.",
    },
    {
      icon: <FaFont />,
      title: "Typography & Text Effects",
      description: "Create stunning text styles and compositions.",
    },
    {
      icon: <FaMagic />,
      title: "Photo Manipulation",
      description: "Blend, warp, and transform images creatively.",
    },
    {
      icon: <FaFileImage />,
      title: "Export & File Formats",
      description: "Save and export projects for print or digital use.",
    },
  ];

  const modules = [
    {
      title: "Introduction to Adobe Flash",
      icon: <MonitorPlay className="w-6 h-6 text-pink-500" />,
      desc: "Explore the Flash interface, panels, and essential tools for animation and design.",
    },
    {
      title: "Drawing & Design Tools",
      icon: <Palette className="w-6 h-6 text-purple-500" />,
      desc: "Master vector drawing, shapes, colors, and gradients for creative content.",
    },
    {
      title: "Animation Techniques",
      icon: <Film className="w-6 h-6 text-orange-500" />,
      desc: "Learn frame-by-frame, tweening, and advanced motion animations.",
    },
    {
      title: "Symbols & Layers",
      icon: <Layers className="w-6 h-6 text-pink-600" />,
      desc: "Organize projects with movie clips, buttons, and layers effectively.",
    },
    {
      title: "Interactivity with ActionScript",
      icon: <MousePointer className="w-6 h-6 text-purple-600" />,
      desc: "Add actions, triggers, and user interactivity with ActionScript basics.",
    },
    {
      title: "Publishing Projects",
      icon: <Zap className="w-6 h-6 text-orange-600" />,
      desc: "Export animations, games, and interactive designs for the web & apps.",
    },
  ];

  const projects = [
    {
      title: "Interactive Banner Ads",
      img: IMAGES.design,
      desc: "Design eye-catching ads that respond to user interaction.",
    },
    {
      title: "Animated Short Clips",
      img: IMAGES.animation,
      desc: "Create frame-by-frame and tweened animations for storytelling.",
    },
    {
      title: "Mini Games",
      img: IMAGES.hero,
      desc: "Build engaging Flash-based games with ActionScript.",
    },
  ];

  const careers = [
    {
      title: "Multimedia Designer",
      desc: "Design interactive and visual content.",
      icon: <Star className="w-8 h-8 text-yellow-400" />,
    },
    {
      title: "2D Animator",
      desc: "Animate characters and scenes for media.",
      icon: <Play className="w-8 h-8 text-green-400" />,
    },
    {
      title: "Web Animator",
      desc: "Create web-based animations and interactions.",
      icon: <MonitorPlay className="w-8 h-8 text-blue-400" />,
    },
    {
      title: "E-learning Content Developer",
      desc: "Design educational interactive modules.",
      icon: <ImageIcon className="w-8 h-8 text-purple-400" />,
    },
    {
      title: "Game Designer",
      desc: "Design game mechanics and visual assets.",
      icon: <Zap className="w-8 h-8 text-pink-400" />,
    },
    {
      title: "Interactive Media Specialist",
      desc: "Build immersive digital media experiences.",
      icon: <MousePointer className="w-8 h-8 text-orange-400" />,
    },
  ];

  const futureScope = [
    {
      icon: "🎬",
      title: "Pixar",
      text: "Uses Adobe for animation and post-production.",
    },
    {
      icon: "🎥",
      title: "DreamWorks",
      text: "Integrates Adobe tools in movie workflows.",
    },
    {
      icon: "🌌",
      title: "Industrial Light & Magic (ILM)",
      text: "Relies on Adobe for VFX editing.",
    },
    {
      icon: "🎮",
      title: "Blizzard Entertainment",
      text: "Uses Adobe in game cinematic production.",
    },
    {
      icon: "🕹️",
      title: "Ubisoft",
      text: "Employs Adobe for 3D art and game assets.",
    },
    {
      icon: "⚽",
      title: "Electronic Arts (EA)",
      text: "Leverages Adobe for sports game visuals.",
    },
    {
      icon: "🎭",
      title: "Sony Pictures Animation",
      text: "Uses Adobe in animated film production.",
    },
    {
      icon: "🖥️",
      title: "Weta Digital",
      text: "Incorporates Adobe for post-production work.",
    },
    {
      icon: "💻",
      title: "Autodesk",
      text: "Partners with Adobe for design workflows.",
    },
    {
      icon: "👟",
      title: "Nike",
      text: "Uses Adobe for marketing and branding visuals.",
    },
    {
      icon: "🚗",
      title: "Mercedes-Benz",
      text: "Creates campaigns with Adobe Creative Suite.",
    },
    {
      icon: "🥤",
      title: "Coca-Cola",
      text: "Designs ads and packaging using Adobe.",
    },
    {
      icon: "🔍",
      title: "Google",
      text: "Uses Adobe for creative design and branding.",
    },
    {
      icon: "🍏",
      title: "Apple",
      text: "Employs Adobe tools for promotional visuals.",
    },
    {
      icon: "🏰",
      title: "Disney",
      text: "Creates digital media with Adobe software.",
    },
  ];

  const usageAreas = [
    {
      icon: <FaPaintBrush />,
      title: "Photoshop Basics",
      description:
        "Master image editing, retouching, and creative compositions.",
    },
    {
      icon: <FaPenNib />,
      title: "Illustrator Design",
      description: "Create stunning vector graphics, logos, and illustrations.",
    },
    {
      icon: <FaVideo />,
      title: "Premiere Pro Editing",
      description:
        "Edit professional videos with smooth transitions and effects.",
    },
    {
      icon: <FaMagic />,
      title: "After Effects Animation",
      description: "Design motion graphics and cinematic visual effects.",
    },
    {
      icon: <FaCube />,
      title: "Adobe Dimension",
      description: "Create realistic 3D product mockups and branding visuals.",
    },
    {
      icon: <FaRunning />,
      title: "Lightroom Photography",
      description: "Enhance and color-correct your photos like a pro.",
    },
  ];

  return (
    <div className="bg-white text-slate-900 min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-pink-500 via-orange-400 to-purple-500 text-white py-24">
        <img
          src={IMAGES.hero}
          alt="Adobe Flash"
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <h1
            data-aos="fade-up"
            className="text-5xl sm:text-6xl font-extrabold drop-shadow-lg animate-float"
          >
            Adobe Professional Course
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            className="mt-6 max-w-2xl mx-auto text-lg text-pink-50"
          >
            Learn multimedia design, interactive animations, and ActionScript
            programming to bring your creative ideas to life.
          </p>
          <div
            data-aos="fade-up"
            data-aos-delay="300"
            className="mt-8 flex justify-center gap-4"
          >
            <Link
              to="/contact"
              className="rounded-full bg-white text-pink-600 px-6 py-3 font-semibold shadow-lg hover:scale-105 transition"
            >
              Start Learning
            </Link>
            <Link
              to="/contact"
              className="rounded-full border border-white/40 px-6 py-3 font-semibold text-white shadow-lg hover:scale-105 transition"
            >
              Talk to Advisor
            </Link>
          </div>
        </div>
      </section>

      <section
        style={{
          padding: "60px 20px",
          maxWidth: "1200px",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontSize: "2.5rem",
            fontWeight: "bold",
            color: "#333",
          }}
          data-aos="fade-up"
        >
          Topics You Will Learn
        </h2>

        <div
          style={{
            display: "flex",
            gap: "40px",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {/* Left: Topics with hover + AOS */}
          <div style={{ flex: "1", minWidth: "300px", maxWidth: "600px" }}>
            {topics.map((topic, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: "15px",
                  alignItems: "flex-start",
                  padding: "15px",
                  marginBottom: "15px",
                  background: "#f8f8f8",
                  borderRadius: "10px",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  cursor: "pointer",
                }}
                data-aos="fade-up"
                data-aos-delay={i * 100}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 20px rgba(89, 141, 201, 0.89)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{ fontSize: "28px", color: "#279dd0ff" }}
                  className="animate-bounce"
                >
                  {topic.icon}
                </div>
                <div>
                  <h3
                    style={{
                      margin: "0 0 5px",
                      fontWeight: "bold",
                      color: "black",
                    }}
                  >
                    {topic.title}
                  </h3>
                  <p style={{ margin: 0, fontSize: "14px", color: "#555" }}>
                    {topic.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Image */}
          <div
            style={{
              flex: "1",
              minWidth: "300px",
              display: "flex",
              flexDirection: "column", // stack images vertically
              justifyContent: "center",
              alignItems: "center",
              gap: "20px", // space between images
            }}
            data-aos="fade-left"
          >
            <img
              src="/assets/88135384-awesome-adobe-photohop-wallpaper.png"
              alt="Adobe Photoshop"
              style={{
                width: "100%",
                maxWidth: "450px",
                borderRadius: "15px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            />
            <img
              src="/assets/photoshop-intro.jpg"
              alt="Adobe Photoshop"
              style={{
                width: "100%",
                maxWidth: "450px",
                borderRadius: "15px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            />
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div data-aos="fade-right">
            <img
              src={IMAGES1.workflow}
              alt="Workflow"
              className="rounded-xl shadow-lg animate-float"
            />
          </div>
          <div data-aos="fade-left">
            <h3 className="text-2xl font-bold">Creative Workflow</h3>
            <ol className="mt-6 space-y-4 animate-float">
              {workflow.map((step, i) => (
                <li
                  key={i}
                  className="flex gap-4 items-start animate-slideInRight"
                  style={{ animationDelay: `${i * 0.3}s` }}
                >
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold shadow">
                    {i + 1}
                  </span>
                  <p className="text-slate-700">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 data-aos="fade-up" className="text-3xl font-bold text-center mb-10">
          Course Modules
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 animate-shake">
          {modules.map((module, i) => (
            <div
              key={i}
              data-aos="zoom-in"
              data-aos-delay={i * 100}
              className="p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 shadow hover:shadow-xl hover:scale-105 transition relative overflow-hidden"
            >
              {/* Bottom-right corner gradient */}
              <div className="absolute bottom-0 right-0 w-8 h-8 bg-gradient-to-br from-pink-500 to-transparent pointer-events-none"></div>

              <div className="flex items-center gap-3">
                {/* Icon with colored background and rounded border */}
                <div
                  className="p-3 rounded-full flex items-center justify-center animate-zoom-out"
                  style={{ backgroundColor: "#a0c4ff" }} // mild blue
                >
                  {module.icon}
                </div>

                <h3 className="font-semibold text-lg">{module.title}</h3>
              </div>
              <p className="mt-3 text-slate-600 text-sm">{module.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Projects Showcase */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 data-aos="fade-up" className="text-3xl font-bold text-center mb-10">
          Hands-On Projects
        </h2>
        <div className="grid md:grid-cols-3 gap-8 animate-bounce-slow">
          {projects.map((proj, i) => (
            <div
              key={i}
              data-aos="flip-left"
              data-aos-delay={i * 200}
              className="rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition relative p-2"
            >
              {/* Corner lines */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-pink-500"></div>
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-pink-500"></div>

              <img
                src={proj.img}
                alt={proj.title}
                className="h-40 w-full object-cover rounded-lg"
              />
              <div className="p-5 bg-white rounded-b-xl">
                <h3 className="font-semibold">{proj.title}</h3>
                <p className="text-sm text-slate-600 mt-2">{proj.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-5 bg-light">
        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontSize: "2.5rem",
            fontWeight: "bold",
            color: "#333",
          }}
          data-aos="fade-up"
        >
          Where It Can Be Used
        </h2>
        <div className="container">
          <div className="row g-4">
            {usageAreas.map((area, i) => {
              const colors = [
                "#ff9800",
                "#4caf50",
                "#2196f3",
                "#e91e63",
                "#9c27b0",
                "#00bcd4",
              ];
              return (
                <div
                  key={i}
                  className="col-sm-6 col-lg-4"
                  data-aos="zoom-in-up"
                  data-aos-delay={i * 100}
                >
                  <div
                    className="p-4 text-center rounded position-relative h-100"
                    style={{
                      background: "#fff",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                      animation: "shake 3s infinite ease-in-out",
                    }}
                  >
                    {/* colorful corner */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "45px",
                        height: "45px",
                        background: `linear-gradient(135deg, ${
                          colors[i % colors.length]
                        }, transparent)`,
                        borderTopRightRadius: "15px",
                        boxShadow: `0 0 10px ${colors[i % colors.length]}`,
                      }}
                    ></div>
                    <div
                      style={{
                        fontSize: "2rem",
                        color: colors[i % colors.length],
                      }}
                    >
                      {area.icon}
                    </div>
                    <h5 className="fw-bold text-black ">{area.title}</h5>
                    <p className="text-muted small">{area.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Careers */}
      <section className="bg-gradient-to-r from-pink-500 via-orange-400 to-purple-500 text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2
            data-aos="fade-up"
            className="text-3xl font-bold text-center mb-10"
          >
            Career Opportunities
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-float">
            {careers.map((job, i) => (
              <div
                key={i}
                data-aos="zoom-in"
                data-aos-delay={i * 150}
                className="p-6 rounded-xl bg-white/10 backdrop-blur-md hover:bg-white/20 transition flex flex-col items-center text-center"
              >
                {/* Icon with rounded background */}
                <div className="bg-white/20 p-4 rounded-full mb-3 flex items-center justify-center border border-white/30 animate-wiggle">
                  {job.icon}
                </div>

                <h3 className="font-semibold mt-3">{job.title}</h3>
                <p className="text-sm mt-2">{job.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: 0, margin: 0, textAlign: "center" }}>
        <h1
          style={{
            textAlign: "center",
            marginBottom: "20px",
            paddingTop: "40px",
            fontSize: "2.5rem",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          We Are Going To Work In
        </h1>

        {/* Image */}
        <img
          src={AdobePic} // your Photoshop image import
          alt="Adobe Photoshop Everywhere"
          data-aos="zoom-in"
          style={{
            width: "100%",
            height: "auto",
            maxHeight: "80vh",
            objectFit: "cover",
            borderRadius: "10px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        />

        {/* Content */}
        <p
          style={{
            maxWidth: "900px",
            margin: "30px auto 0",
            fontSize: "1.2rem",
            lineHeight: "1.6",
            color: "#444",
            padding: "0 20px",
          }}
        >
          Photoshop goes everywhere you go. Take your creativity to new places.
          Crop and expand images, adjust colour, add and remove objects and more
          with Photoshop on desktop, web and mobile. Get them all with a
          Photoshop plan or download the mobile app for free.
        </p>
      </section>

      {/* future scope */}

      <section
        style={{
          padding: "60px 20px",
          background: "#fff",
          color: "#333",
          textAlign: "center",
          overflow: "hidden", // important to hide scroll overflow
        }}
      >
        <h2
          style={{
            fontSize: "2.5rem",
            fontWeight: "bold",
            marginBottom: "30px",
          }}
          data-aos="fade-up"
        >
          Future Scope
        </h2>

        <div
          id="my-sc"
          style={{
            display: "flex",
            width: "100%",
            overflow: "hidden",
          }}
        >
          {[0, 1].map((groupIndex) => (
            <div
              key={groupIndex}
              className="scroll-group"
              style={{
                display: "flex",
                gap: "20px",
                flexShrink: 0,
              }}
            >
              {futureScope.map((item, i) => {
                const colors = ["#ff5722", "#673ab7", "#3f51b5", "#009688"];
                return (
                  <div
                    key={i}
                    style={{
                      position: "relative",
                      background: "linear-gradient(145deg, #fdfdfd, #f3f3f3)",
                      borderRadius: "20px",
                      padding: "25px",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                      border: "1px solid #eee",
                      transition: "transform 0.3s ease",
                      minWidth: "250px",
                      flexShrink: 0,
                      animation: "shake 3s infinite ease-in-out",
                      animationDelay: `${i * 500}ms`,
                      color: "#333",
                    }}
                    data-aos="fade-up"
                    data-aos-delay={i * 150}
                  >
                    {/* colorful corner */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "35px",
                        height: "35px",
                        background: `linear-gradient(135deg, ${
                          colors[i % colors.length]
                        }, transparent)`,
                        borderTopRightRadius: "20px",
                        boxShadow: `0 0 10px ${colors[i % colors.length]}`,
                      }}
                    ></div>

                    <div style={{ fontSize: "2rem", marginBottom: "10px" }}>
                      {item.icon}
                    </div>
                    <h3
                      style={{
                        fontSize: "1.3rem",
                        fontWeight: "bold",
                        marginBottom: "10px",
                      }}
                    >
                      {item.title}
                    </h3>
                    <p style={{ fontSize: "0.95rem", color: "#555" }}>
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <style jsx>{`
          #my-sc {
            overflow: hidden;
            white-space: nowrap;
          }

          .scroll-group {
            animation: marquee 20s linear infinite;
          }

          @keyframes marquee {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }

          @keyframes shake {
            0%,
            100% {
              transform: translate(0, 0) rotate(0);
            }
            20% {
              transform: translate(-1px, 1px) rotate(-0.5deg);
            }
            40% {
              transform: translate(1px, -1px) rotate(0.5deg);
            }
            60% {
              transform: translate(-1px, 1px) rotate(-0.5deg);
            }
            80% {
              transform: translate(1px, -1px) rotate(0.5deg);
            }
          }
        `}</style>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-6 py-16 bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl mt-10 mb-10">
        <h3 className="text-2xl font-bold mb-6 text-center text-pink-700">
          FAQ
        </h3>
        <div className="space-y-4">
          <details className="p-4 rounded-lg border border-pink-200 bg-pink-50">
            <summary className="cursor-pointer font-medium flex justify-between">
              Do I need prior design knowledge?{" "}
              <ChevronDown className="w-4 h-4" />
            </summary>
            <p className="mt-2 text-sm text-pink-700">
              No, this course starts from the basics and gradually advances to
              professional-level projects.
            </p>
          </details>
          <details className="p-4 rounded-lg border border-pink-200 bg-pink-50">
            <summary className="cursor-pointer font-medium flex justify-between">
              Will I learn ActionScript? <ChevronDown className="w-4 h-4" />
            </summary>
            <p className="mt-2 text-sm text-pink-700">
              Yes, you will learn ActionScript fundamentals to add interactivity
              to your Flash projects.
            </p>
          </details>
          <details className="p-4 rounded-lg border border-pink-200 bg-pink-50">
            <summary className="cursor-pointer font-medium flex justify-between">
              Is this course project-based? <ChevronDown className="w-4 h-4" />
            </summary>
            <p className="mt-2 text-sm text-pink-700">
              Absolutely. Each module includes hands-on projects like banner
              ads, animations, and interactive applications.
            </p>
          </details>
          <details className="p-4 rounded-lg border">
            {" "}
            <summary className="cursor-pointer font-medium flex justify-between">
              {" "}
              How are the classes conducted? <ChevronDown className="w-4 h-4" />{" "}
            </summary>{" "}
            <p className="mt-2 text-sm text-slate-600">
              {" "}
              Classes are conducted in both online and offline modes, including
              live demonstrations and interactive sessions.{" "}
            </p>{" "}
          </details>{" "}
          <details className="p-4 rounded-lg border">
            {" "}
            <summary className="cursor-pointer font-medium flex justify-between">
              {" "}
              Do I need a high-end computer to practice?{" "}
              <ChevronDown className="w-4 h-4" />{" "}
            </summary>{" "}
            <p className="mt-2 text-sm text-slate-600">
              {" "}
              A standard computer with moderate specifications is enough to run
              Adobe Flash smoothly for practice.{" "}
            </p>{" "}
          </details>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-pink-500 via-orange-400 to-purple-500 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center" data-aos="zoom-in">
          <h4 className="text-4xl font-extrabold">
            Ready to Master Adobe Flash?
          </h4>
          <p className="mt-4 max-w-2xl mx-auto text-pink-50 text-lg">
            Build creative skills, design engaging animations, and start your
            career in multimedia and design.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Link
              to="/contact"
              className="rounded-full bg-white text-pink-600 px-6 py-3 font-semibold shadow hover:scale-105 transition"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
